import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCgmDevices,
  useLastMealDate,
  usePatientReports,
  useSubscriptions,
} from './queries';

/**
 * Single source of truth for the "Quick select date range" dropdown that
 * appears next to the patient date pickers. Aggregates four data sources
 * into one grouped list:
 *
 *   • Latest data       — three windows ending at the patient's last meal
 *     (Last meal · Last 7 days · Last 30 days)
 *   • Past periods      — subscriptions (descending by start_date)
 *   • Past reports      — patient_reports (descending by date_until, ≥2022)
 *   • CGM devices       — device first_reading → last_reading windows
 *
 * Empty groups are dropped before render. `lastMealDate` is also returned
 * raw so consumers can render the "Last meal: <date>" chip inline without
 * re-fetching `usePatient`.
 *
 * Each `QuickSelectItem` is identified by an opaque `id` (so React keys
 * stay stable across renders) and carries an ISO `from`/`to` pair the
 * caller can hand directly to `useDateRange.setRange` or any consumer
 * that wants `YYYY-MM-DD` strings.
 */
export type QuickSelectItem = {
  id: string;
  label: string;
  /** ISO YYYY-MM-DD. */
  from: string;
  /** ISO YYYY-MM-DD. */
  to: string;
};

export type QuickSelectGroup = {
  id: 'latest' | 'subscriptions' | 'reports' | 'devices';
  label: string;
  items: QuickSelectItem[];
};

const isoToDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const dateToIso = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const onlyDate = (iso: string | undefined | null): string | null => {
  if (!iso) return null;
  return iso.slice(0, 10);
};

const subDays = (d: Date, n: number): Date => {
  const out = new Date(d);
  out.setDate(out.getDate() - n);
  return out;
};

/**
 * @param patientId — patient context for the queries.
 * @param opts.enabled — when false, the heavy queries (reports, devices) are
 *  skipped. The cheap query that powers the "Last meal" chip stays eager so
 *  callers like `DateRangePicker` can render the chip immediately without
 *  paying the cost of fetching the dropdown contents until the user actually
 *  opens it.
 */
export const useQuickSelectItems = (
  patientId: number | null | undefined,
  opts: { enabled?: boolean } = {},
) => {
  const { t } = useTranslation();
  // Reading from the dedicated `useLastMealDate` hook means the chip in
  // DateRangePicker shows the correct date even before the dropdown is
  // opened. The previous `usePatient().data.last_meal_date` path was always
  // undefined because `GET /patients/{id}` doesn't include that field.
  const lastMeal = useLastMealDate(patientId ?? null);
  const subscriptions = useSubscriptions(patientId ?? 0);
  const heavyEnabled = opts.enabled ?? true;
  // Lazy queries — only fire when the dropdown opens. v1's hot path
  // (just rendering the chip) doesn't need reports or devices.
  const reports = usePatientReports(patientId ?? 0, { enabled: !!patientId && heavyEnabled });
  const devices = useCgmDevices(patientId ?? 0, { enabled: !!patientId && heavyEnabled });

  const lastMealIso = lastMeal.date;

  const latestItems: QuickSelectItem[] = useMemo(() => {
    const lastMeal = isoToDate(lastMealIso);
    if (!lastMeal) return [];
    const endIso = dateToIso(lastMeal);
    return [
      {
        id: 'latest-1d',
        label: t('Last meal · {{date}}', { date: endIso }),
        from: endIso,
        to: endIso,
      },
      {
        id: 'latest-7d',
        label: t('Last 7 days · ending {{date}}', { date: endIso }),
        from: dateToIso(subDays(lastMeal, 6)),
        to: endIso,
      },
      {
        id: 'latest-30d',
        label: t('Last 30 days · ending {{date}}', { date: endIso }),
        from: dateToIso(subDays(lastMeal, 29)),
        to: endIso,
      },
    ];
  }, [lastMealIso, t]);

  const subscriptionItems: QuickSelectItem[] = useMemo(() => {
    const rows = subscriptions.data ?? [];
    const today = dateToIso(new Date());
    return [...rows]
      // Only past + currently-active subs make sense as a click target —
      // a future-dated sub would generate over days that don't exist yet.
      .filter((s) => s.start_date <= today)
      .sort((a, b) => b.start_date.localeCompare(a.start_date))
      .map((s) => {
        // Clamp the end to today for active subs so the "to" never bleeds
        // into the future.
        const to = s.expiration_date > today ? today : s.expiration_date;
        return {
          id: `sub_${s.id}`,
          label: `${s.start_date} → ${to}`,
          from: s.start_date,
          to,
        };
      });
  }, [subscriptions.data]);

  const reportItems: QuickSelectItem[] = useMemo(() => {
    const rows = reports.data ?? [];
    return [...rows]
      .filter((r) => !!r.date_since && !!r.date_until && r.date_since >= '2022-01-01')
      .sort((a, b) => (b.date_until ?? '').localeCompare(a.date_until ?? ''))
      .map((r) => ({
        id: `report_${r.patient_report_id}`,
        label: `${r.date_since} → ${r.date_until}`,
        from: r.date_since,
        to: r.date_until,
      }));
  }, [reports.data]);

  const deviceItems: QuickSelectItem[] = useMemo(() => {
    const rows = devices.data?.rows ?? [];
    return rows
      .map((d) => {
        const from = onlyDate(d.first_reading);
        const to = onlyDate(d.last_reading);
        if (!from || !to) return null;
        // Friendly device name only — never fall back to the raw `device_id`,
        // which is a GUID-style string. When manufacturer/model are missing,
        // just show the date window.
        const friendlyName = [d.manufacturer, d.model].filter(Boolean).join(' ').trim();
        const label = friendlyName ? `${friendlyName} · ${from} → ${to}` : `${from} → ${to}`;
        return { id: `device_${d.device_id}`, label, from, to };
      })
      .filter((d): d is QuickSelectItem => d !== null)
      // Most-recent device window first — matches the sort order of the
      // subscription + report groups so every section reads chronologically
      // descending. v1 returned devices unsorted; we impose the order here.
      .sort((a, b) => b.to.localeCompare(a.to));
  }, [devices.data]);

  const groups: QuickSelectGroup[] = useMemo(() => {
    const all: QuickSelectGroup[] = [
      { id: 'latest', label: t('Latest data'), items: latestItems },
      { id: 'subscriptions', label: t('Past tracking periods'), items: subscriptionItems },
      { id: 'reports', label: t('Past reports'), items: reportItems },
      { id: 'devices', label: t('CGM devices'), items: deviceItems },
    ];
    return all.filter((g) => g.items.length > 0);
  }, [latestItems, subscriptionItems, reportItems, deviceItems, t]);

  const isEmpty = groups.length === 0;

  return {
    groups,
    lastMealDate: lastMealIso,
    isEmpty,
    isLoading: lastMeal.isLoading || subscriptions.isLoading || reports.isLoading || devices.isLoading,
  };
};
